// The Section tab's "Form" group for a selected Form section: which CRM form
// the page embeds (with Edit form, which opens the form in the CRM in a new
// tab, and + New form), the form's status, and what a submit does next.
// Leads land in the CRM with this page as their source.
import type { PageFormRef, Section } from '../model/pages';
import { crmFormEditorPath, type CrmFormSummary } from '../api';
import { PlusIcon } from '../icons';
import { BTN, Block, HINT, SELECT, SelectField, TEXT_BTN } from './InspectorKit';

/** " (draft)" after a draft form's name in pickers. */
export function formTags(f: Pick<CrmFormSummary, 'status'>): string {
  return f.status === 'draft' ? ' (draft)' : '';
}

/** The linked form's status in one line, for the Form group and the Page tab. */
export function formStatusLine(form: Pick<PageFormRef, 'status'> | null | undefined, pageLive: boolean): string {
  if (!form) return '';
  if (form.status === 'archived') return 'This form was deleted. Pick another form.';
  if (form.status === 'draft') return pageLive ? 'Draft. It goes live when you publish the changes.' : 'Draft. It goes live when you publish the page.';
  return 'Live. New answers land in the CRM.';
}

export interface FormGroupProps {
  /** The selected Form section (its after_submit is edited here). */
  section: Section;
  /** Live and draft CRM forms (null while loading). */
  forms: CrmFormSummary[] | null;
  /** The page's embedded form (page.form_id). */
  formId: number | null;
  /** The linked form as the server knows it (page.form), for a form the list no longer has. */
  linkedForm?: PageFormRef | null;
  onFormChange: (formId: number | null) => void;
  /** Creates a draft CRM form named after the page and links it. */
  onNewForm: () => void;
  /** A new form is being created ("Creating…"). */
  formBusy?: boolean;
  /** The page is live (changes the draft note). */
  pageLive?: boolean;
  /** Patches the selected section (updateSelected). */
  onChange: (p: Partial<Section>) => void;
  /** The section sits on the funnel's last step (or the thank-you): the
   *  default after a submit is then the page's thank-you, not a next step. */
  lastStep?: boolean;
}

/** The linked form: from the list, else what the page row says (deleted forms). */
export function resolveLinkedForm(forms: CrmFormSummary[] | null, formId: number | null, linked?: PageFormRef | null): PageFormRef | null {
  if (formId == null) return null;
  const f = forms?.find((x) => x.id === formId);
  if (f) return { id: f.id, slug: f.slug, name: f.name, status: f.status };
  if (linked && linked.id === formId) return linked;
  // Linked, but not among the live and draft forms: archived since.
  return forms ? { id: formId, slug: '', name: 'Deleted form', status: 'archived' } : null;
}

export function FormGroup({ section, forms, formId, linkedForm, onFormChange, onNewForm, formBusy, pageLive = false, onChange, lastStep = false }: FormGroupProps) {
  const current = resolveLinkedForm(forms, formId, linkedForm);
  const listed = !!forms && formId != null && forms.some((f) => f.id === formId);
  const status = formStatusLine(current, pageLive);
  const hasCalendar = !!forms?.find((f) => f.id === formId)?.settings?.calendar_id;
  return (
    <Block name="Form" collapsible={false}
      action={(
        <button type="button" className={TEXT_BTN} disabled={formBusy} onClick={onNewForm}>
          {formBusy ? 'Creating…' : <><PlusIcon size={13} />New form</>}
        </button>
      )}>
      <div className="grid min-w-0 gap-1.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <select className={`${SELECT} min-w-0 flex-1`} aria-label="Form" value={formId ?? ''}
            onChange={(e) => onFormChange(e.target.value ? Number(e.target.value) : null)}>
            <option value="">{forms === null ? 'Loading forms…' : 'None'}</option>
            {current && !listed && <option value={current.id}>{current.name}{current.status === 'archived' ? ' (deleted)' : ''}</option>}
            {(forms ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}{formTags(f)}</option>)}
          </select>
          {current && current.status !== 'archived' && (
            <a className={`${BTN} inline-flex items-center`} href={crmFormEditorPath(current.id)} target="_blank" rel="noreferrer"
              title="Opens the form in the CRM, in a new tab">Edit form</a>
          )}
        </div>
        {status && <p className={`${HINT} ${current?.status === 'archived' ? '!text-danger' : ''}`} data-form-status={current?.status}>{status}</p>}
      </div>
      <SelectField label="After submit" value={section.after_submit ?? (lastStep ? 'thank_you' : 'next')}
        onChange={(v) => onChange({ after_submit: v as Section['after_submit'] })}>
        {!lastStep && <option value="next">Next step{section.after_submit ? '' : ' (default)'}</option>}
        <option value="thank_you">Thank you page{lastStep && !section.after_submit ? ' (default)' : ''}</option>
        <option value="form">The form’s own end screen</option>
      </SelectField>
      {hasCalendar && <p className={HINT}>This form ends with its calendar, so the page moves on after a booking.</p>}
    </Block>
  );
}
